import { motion } from "framer-motion";
import { Reveal } from "../lib/motionx";
import { SectionLabel } from "./PageHeader";
import { useLang } from "../i18n";

const stages = [
  { img: "/brand/m_caterpillar.png", label: "Caterpillar", note: "Where every journey begins" },
  { img: "/brand/m_chrysalis.png", label: "Chrysalis", note: "A safe space to grow" },
  { img: "/brand/m_emerging.png", label: "Emerging", note: "Finding new strength" },
  { img: "/brand/m_butterfly.png", label: "Butterfly", note: "Ready to take flight" },
];

export const GrowthJourney = () => {
  const { t } = useLang();
  return (
    <section className="py-20 sm:py-28 overflow-hidden bg-gradient-to-b from-sage/25 via-sage/10 to-transparent">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="text-center max-w-2xl mx-auto">
          <SectionLabel className="mb-5 justify-center">{t("Every child's journey")}</SectionLabel>
          <h2 className="font-heading font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-ink">
            {t("Growth, one gentle step at a time")}
          </h2>
          <p className="mt-4 text-lg text-ink/60 leading-relaxed">
            {t("Just like a caterpillar becomes a butterfly, every child grows at their own pace — and we're here to nurture each stage of the way.")}
          </p>
        </Reveal>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.22 } } }}
          className="relative mt-16 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-4"
        >
          {/* dotted connecting path (desktop) */}
          <div
            aria-hidden="true"
            className="hidden md:block absolute top-[68px] left-[12.5%] right-[12.5%] border-t-2 border-dashed border-teal/30"
          />

          {stages.map((s, i) => (
            <motion.div
              key={s.label}
              variants={{
                hidden: { opacity: 0, y: 24 },
                show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
              }}
              className="relative flex flex-col items-center text-center"
            >
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3.2 + i * 0.3, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-[1] grid place-items-center h-32 w-32 sm:h-36 sm:w-36 rounded-full bg-white shadow-[0_16px_40px_rgba(58,118,166,0.12)] ring-1 ring-teal/5"
              >
                <img
                  src={s.img}
                  alt={`Illustration of the ${s.label.toLowerCase()} stage, in the Amenity butterfly style`}
                  className="h-[74%] w-[74%] object-contain"
                  loading="lazy"
                />
              </motion.div>
              <span className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal/10 text-teal text-xs font-medium px-3 py-1">
                {t("Step")} {i + 1}
              </span>
              <h3 className="mt-3 font-heading font-semibold text-lg text-ink">{t(s.label)}</h3>
              <p className="mt-1 text-sm text-ink/55 max-w-[16ch]">{t(s.note)}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
